import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  ViewChild,
  afterNextRender,
  computed,
  effect,
  inject,
  Injector,
  input,
  output,
  runInInjectionContext,
  signal,
  untracked,
} from '@angular/core';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-expandable-order-search',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './expandable-order-search.component.html',
  styleUrls: ['./expandable-order-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpandableOrderSearchComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);

  readonly value = input('');
  readonly placeholder = input('Search by order ID or order number...');
  readonly isNativeInput = Capacitor.isNativePlatform();

  readonly valueChange = output<string>();
  readonly searchSubmit = output<string>();
  readonly cleared = output<void>();

  readonly isExpanded = signal(false);
  private readonly isCompactUi = signal(this.detectCompactUi());

  readonly displayPlaceholder = computed(() =>
    this.isCompactUi()
      ? 'Search by order ID'
      : this.placeholder(),
  );

  @HostBinding('class.expandable-order-search-host--expanded')
  get hostExpanded(): boolean {
    return this.isExpanded();
  }

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  constructor() {
    effect(() => {
      const term = this.value().trim();
      if (term) {
        this.isExpanded.set(true);
      }
    });

    effect(() => {
      if (this.isExpanded()) {
        untracked(() => this.scheduleFocusAfterRender());
      }
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.isCompactUi.set(this.detectCompactUi());
  }

  private detectCompactUi(): boolean {
    if (typeof window === 'undefined') {
      return true;
    }
    if (Capacitor.isNativePlatform()) {
      return true;
    }
    return window.innerWidth < 768;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target || !this.isExpanded()) {
      return;
    }
    if (!this.host.nativeElement.contains(target) && !this.value().trim()) {
      this.isExpanded.set(false);
    }
  }

  toggleExpand(): void {
    if (this.isExpanded()) {
      return;
    }
    this.isExpanded.set(true);
  }

  onInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value ?? '';
    this.valueChange.emit(next);
  }

  onEnter(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    const term = (event.target as HTMLInputElement).value?.trim() ?? '';
    if (!term) {
      return;
    }
    this.searchSubmit.emit(term);
  }

  clear(): void {
    this.valueChange.emit('');
    this.cleared.emit();
    this.isExpanded.set(false);
  }

  private scheduleFocusAfterRender(): void {
    runInInjectionContext(this.injector, () => {
      afterNextRender(() => {
        this.focusInputElement();
      });
    });
  }

  private focusInputElement(): void {
    const attemptFocus = (): boolean => {
      const input = this.searchInput?.nativeElement;
      if (!input) {
        return false;
      }

      input.focus();

      if (this.isNativeInput) {
        // Android WebView often ignores focus() unless triggered like a user tap.
        input.click();
      }

      try {
        const end = input.value.length;
        input.setSelectionRange(end, end);
      } catch {
        // setSelectionRange can fail on some mobile input types
      }

      return document.activeElement === input;
    };

    if (attemptFocus()) {
      return;
    }

    const retryDelays = this.isNativeInput ? [50, 120, 220] : [16];
    for (const delay of retryDelays) {
      setTimeout(() => attemptFocus(), delay);
    }
  }
}
